import type {
  GuidebookData,
  GuidebookPlace,
  GuidebookHouseRules,
  GuidebookWelcome,
  GuidebookWeather,
  GuidebookHighlight,
} from "../types"

export type ConciergeAccessState = "public" | "authenticated" | "ical-locked"

export interface ConciergeData {
  propertyName: string
  address: string
  heroMessage: string | null
  wifiName: string
  wifiPassword: string
  doorCode: string
  doorCodeVisibility: "available_now" | "available_on_arrival" | "locked" | "not_configured"
  doorCodePolicyText: string | undefined
  checkinTime: string
  checkoutTime: string | null
  checkInDate: string | null
  checkOutDate: string | null
  nights: number
  places: GuidebookPlace[]
  houseRules: GuidebookHouseRules
  welcome: GuidebookWelcome | null
  weather: GuidebookWeather | null
  highlights: GuidebookHighlight[]
  accessState: ConciergeAccessState
  requiresPin: boolean
}

export function deriveAccessState(data: GuidebookData): ConciergeAccessState {
  if (!data.is_public) return "authenticated"
  const sensitiveLocked =
    Array.isArray((data as { sensitiveFields?: unknown[] }).sensitiveFields) &&
    (data as { sensitiveFields: unknown[] }).sensitiveFields.length > 0
  if (sensitiveLocked && (data as { authMode?: string }).authMode === "ical") return "ical-locked"
  return "public"
}

function deriveDoorCodeVisibility(
  data: GuidebookData
): ConciergeData["doorCodeVisibility"] {
  const hint = (data as { doorCodeVisibility?: string }).doorCodeVisibility
  if (hint === "available_now" || hint === "available_on_arrival" || hint === "locked") {
    return hint
  }
  const status = data.property.door_code_status
  if (status === "pending") return "available_on_arrival"
  if (status === "visible") return "available_now"
  if (status === "not_configured" || !data.property.door_code) return "not_configured"
  return "locked"
}

export function adaptToConciergeData(data: GuidebookData): ConciergeData {
  const pub = data as { authMode?: string; requiresPinForSensitive?: boolean; doorCodePolicyText?: string }
  return {
    propertyName: data.property.name,
    address: data.property.address,
    heroMessage: data.welcome?.hero_message ?? null,
    wifiName: data.property.wifi_name,
    wifiPassword: data.property.wifi_password,
    doorCode: data.property.door_code,
    doorCodeVisibility: deriveDoorCodeVisibility(data),
    doorCodePolicyText: pub.doorCodePolicyText,
    checkinTime: data.property.checkin_time,
    checkoutTime: data.houseRules?.checkout?.checkoutTime ?? null,
    checkInDate: data.strategy?.check_in ?? null,
    checkOutDate: data.strategy?.check_out ?? null,
    nights: data.strategy?.nights ?? 0,
    places: data.places,
    houseRules: data.houseRules,
    welcome: data.welcome,
    weather: data.weather,
    highlights: data.highlights ?? [],
    accessState: deriveAccessState(data),
    requiresPin: Boolean(pub.requiresPinForSensitive),
  }
}
